import { useEffect, useRef } from "react"
import { Draft } from "immer"
import { useImmerReducer } from "use-immer"
import { useUpdateEffect } from "react-use"
import { DropdownState, DropdownAction, RootContextType } from "./types"
import { useOnClickOutside } from "../utils/useOnClickOutside"
import { createContext } from "../utils/createContext"
import StyledContainer from "./styles/ContainerStyles"

const [Provider, useDropdown] = createContext<RootContextType>(`Dropdown`)

type RootOwnProps = Readonly<{
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onValueSelect?: (value: string) => void
}>

export type RootProps = RootOwnProps & React.ComponentPropsWithoutRef<typeof StyledContainer>

const Root: React.FC<RootProps> = ({
  open,
  onOpenChange,
  onValueSelect,
  children,
  defaultOpen = false,
  ...containerProps
}: RootProps) => {
  const [state, dispatch] = useImmerReducer<DropdownState, DropdownAction>(reducer, { open: open ?? defaultOpen })

  useEffect(() => {
    if (open === true) {
      dispatch({ type: `open` })
    } else {
      dispatch({ type: `close` })
    }
  }, [open, dispatch])

  useUpdateEffect(() => {
    onOpenChange?.(state.open)
  }, [state.open])

  useEffect(() => {
    if (state.selectedItem) {
      onValueSelect?.(state.selectedItem)
    }
  }, [state.selectedItem]) // @ToDo onValueSelect should be a dependency here, but adding it causes maximum update depth to be exceeded

  const containerRef = useRef(null)

  useOnClickOutside(containerRef, () => dispatch({ type: `close` }))

  return (
    <StyledContainer ref={containerRef} {...containerProps}>
      <Provider state={state} dispatch={dispatch}>
        {children}
      </Provider>
    </StyledContainer>
  )
}

function reducer(state: Draft<DropdownState>, action: DropdownAction): DropdownState {
  switch (action.type) {
    case `open`:
      return { ...state, open: true }
    case `close`:
      return { ...state, open: false }
    case `selectItem`:
      return { ...state, selectedItem: action.item, open: false }
    default:
      return state
  }
}

Root.displayName = `DropdownRoot`

export { Root, useDropdown }
